import axios from 'utils/axios';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import {
    Alert,
    Autocomplete,
    Backdrop,
    Button,
    Checkbox,
    Box,
    CircularProgress,
    Collapse,
    Container,
    FormControlLabel,
    FormGroup,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Switch,
    IconButton,
    Stack
} from '@mui/material';
import { LoadingButton, TabContext, TabPanel } from '@mui/lab';
import TakeAttendanceForm from './TakeAttendanceForm';
import MainCard from 'ui-component/cards/MainCard';
import GradeStudentsForm from './gradeStudentsForm';
import { IconLock } from '@tabler/icons';
import { openSnackbar } from 'store/slices/snackbar';
import TutoringOrMentoringSession from './tutoringOrMentoringSession';
import { useDispatch } from 'store';
import TeacherAbsentSection from './TeacherAbsentSection';
import { showErrorAlert, parseErrorResponse } from 'utils/global-functions';
import getStandardBillingDeadline from 'utils/dateUtils';
import SignContractAlert from './SignContractAlert';

// A saved attendance row counts as marked unless it is "Attendance Not Taken" (0)
const isMarked = (student) => Boolean(student.attendance) && student.attendance.attendance !== 0;

const StandardSession = ({
    session,
    setSession,
    closeSession,
    loadingSession,
    setLoadingSession,
    substituteTeachers,
    socialEmotionalSkills
}) => {
    const [tab, setTab] = useState('plan');
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [plan, setPlan] = useState(null);
    const [isSavingSessionPlan, setIsSavingSessionPlan] = useState(false);
    const [isSavingTeacherAttendance, setIsSavingTeacherAttendance] = useState(false);
    const loading = open && options.length === 0;
    const [attendanceIncomplete, setAttendanceIncomplete] = useState(false);
    const [attendanceCompleted, setAttendanceCompleted] = useState(false);
    const [performanceCompleted, setPerformanceCompleted] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [teacherAbsent, setTeacherAbsent] = useState(false);
    const [sessionConfirmed, setSessionConfirmed] = useState(false);
    const [isFutureSession, setIsFutureSession] = useState(false);
    const [prepTimeCompleted, setPrepTimeCompleted] = useState(false);
    const [lessonPlanChanged, setLessonPlanChanged] = useState(false);
    const [lessonPlanChangedLocally, setLessonPlanChangedLocally] = useState(false);
    const [hasStudents, setHasStudents] = useState(false);
    const [isSavingAttendance, setIsSavingAttendance] = useState(false);
    const [teacherAbsentSaveIsDirty, setTeacherAbsentSaveIsDirty] = useState(false);
    const [closeSessionDialogOpen, setCloseSessionDialogOpen] = useState(false);
    const [isPastBillingDeadline, setIsPastBillingDeadline] = useState(false);

    const dispatch = useDispatch();
    const { state } = useLocation();

    const handlePlanChange = (event, newValue) => {
        if (!newValue) {
            setPlan('');
        } else {
            setPlan({ ...newValue });
        }
        setLessonPlanChangedLocally(true);
    };

    const updatePrepTimeCompleted = async (value) => {
        setIsSavingTeacherAttendance(true);
        try {
            const { data } = await axios.post('/Session/SubmitPrepTimeCompleted', null, {
                params: { sessionId: session.id, completed: value }
            });

            setSession(data);
            setPrepTimeCompleted(value);
            setIsSavingTeacherAttendance(false);
        } catch (err) {
            console.error(err);
            showErrorAlert(err);
        }
    };
    const handlePrepSwitchChanged = () => {
        const prepTimeCompleted = !session.prepTimeCompleted;
        setSession({ ...session, prepTimeCompleted });
        updatePrepTimeCompleted(prepTimeCompleted);
    };
    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    useEffect(() => {
        async function getSession() {
            setHasStudents(session === '' || session.students.length !== 0);
            const startDate = new Date(session.start);
            let pastBillingDeadline = false;
            const today = new Date();
            const satmarSchool = '2574b80d-2c3b-e711-8117-e0071b6af211';

            if (localStorage.roles?.includes('Supervisor')) {
                today.setSeconds(0);
                const lastDateToSubmit = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 8, 23, 59, 0);
                pastBillingDeadline = today > lastDateToSubmit;
            } else if (!localStorage.roles?.includes('Admin') && session.schoolId !== satmarSchool) {
                pastBillingDeadline = startDate < getStandardBillingDeadline();
            }
            setIsPastBillingDeadline(pastBillingDeadline);
            setIsFutureSession(
                new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()) >
                    new Date(today.getFullYear(), today.getMonth(), today.getDate())
            );
            setPlan(session.lessonPlan);

            // The server decides when the session's attendance is complete
            setAttendanceCompleted(Boolean(session.attendanceTaken));

            // If all students have either a score, or are absent, then performance is complete.
            if (session.students.every((s) => s.score != null || s.attendance?.attendance === 1)) {
                setPerformanceCompleted(true);
            }

            setTeacherAbsent(session.teacherAbsent === 1);
            setPrepTimeCompleted(session.prepTimeCompleted);
            setSessionConfirmed(session.supervisorConfirmed);

            setLoadingSession(false);
        }

        getSession();
    }, []);

    useEffect(() => {
        let active = true;

        if (!loading) {
            return undefined;
        }

        (async () => {
            const { data } = await axios.get(`/lessonlibrary/list/${session.id}`);
            if (active) {
                setOptions(data.filter((l) => !l.hasCoachingSessions || l.id === plan.id));
            }
        })();

        return () => {
            active = false;
        };
    }, [loading]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    // Students waiting on an assessment score stay unmarked; the others are submitted
    const blockedStudents = session?.students?.filter((s) => s.assessmentBlocked) ?? [];
    // Only those with no saved mark are listed as "can't be taken"; a mark saved before the block stays
    const blockedUnmarked = blockedStudents.filter((s) => !isMarked(s));
    // Last name first, as in the table, separated by semicolons so each name reads as one
    const blockedNames = blockedUnmarked
        .map((s) => [`${s.studentLastName},`, s.studentFirstName, s.studentMiddleName].filter(Boolean).join(' '))
        .join('; ');
    // Grading needs attendance saved for at least one student, not the whole session marked as taken
    const canGrade = Boolean(session?.attendanceTaken || session?.students?.some(isMarked));
    const everyoneBlocked = session?.students?.length > 0 && session.students.every((s) => s.assessmentBlocked);
    let performanceLockReason = '';
    if (!canGrade) {
        performanceLockReason = everyoneBlocked
            ? `Attendance can't be taken for any student in this session until their Term ${session.assessmentTerm} assessment score is entered`
            : 'You must take attendance before grading the students';
    }

    // Reloads the session, e.g. after the server refused a submit because a student became blocked since it was opened
    const reloadSession = async () => {
        try {
            const { data } = await axios.get(`/Session/${session.id}`);
            setSession(data);
            setAttendanceCompleted(Boolean(data.attendanceTaken));
        } catch (err) {
            console.error(err);
        }
    };

    const onSubmitAttendance = async (students) => {
        const markable = students.filter((s) => !s.assessmentBlocked);
        if (markable.some((s) => s.attendance === 0)) {
            setAttendanceIncomplete(true);
            return;
        }

        setAttendanceIncomplete(false);

        try {
            const { data } = await axios.post('/Session/SubmitAttendance', { Students: markable, SessionId: session.id });

            // Blocked students who were marked before the block started keep their mark. Form values are numbers; 0 is unmarked.
            const unmarked = students.filter((s) => s.assessmentBlocked && !s.attendance).length;
            dispatch(
                openSnackbar({
                    open: true,
                    message:
                        unmarked > 0
                            ? `Attendance submitted. ${unmarked} student${unmarked > 1 ? 's' : ''} without a Term ${
                                  session.assessmentTerm
                              } score ${unmarked > 1 ? 'were' : 'was'} left unmarked.`
                            : 'Attendance submitted successfully!',
                    variant: 'alert',
                    alert: {
                        color: unmarked > 0 ? 'warning' : 'success'
                    },
                    close: false
                })
            );

            setAttendanceCompleted(Boolean(data.attendanceTaken));
            setSession(data);
            setTab('performance');
        } catch (err) {
            console.error(err);
            dispatch(
                openSnackbar({
                    open: true,
                    message: parseErrorResponse(err),
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            // Show who is blocked now, so a retry isn't refused the same way
            await reloadSession();
        }
    };

    const onCancel = () => {
        if (teacherAbsentSaveIsDirty) {
            setCloseSessionDialogOpen(true);
        } else {
            closeSession();
        }
    };

    const onSavePlan = async () => {
        try {
            setIsSavingSessionPlan(true);
            await axios.post('/Session/SubmitlessonPlan', { sessionId: session.id, lessonPlanId: plan.id }).then(console.log('saved!'));
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Lesson plan submitted successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            setLessonPlanChanged(true);
        } catch (err) {
            console.error(err);
            dispatch(
                openSnackbar({
                    open: true,
                    message: parseErrorResponse(err),
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        } finally {
            setIsSavingSessionPlan(false);
        }
    };

    const onSubmitScores = async (studentScores) => {
        try {
            await axios.post('/Session/SubmitScores', { sessionId: session.id, scores: studentScores }).then(console.log('saved!'));
            setPerformanceCompleted(true);
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'Performance submitted successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            closeSession();
        } catch (err) {
            console.error(err);
            dispatch(
                openSnackbar({
                    open: true,
                    message: parseErrorResponse(err),
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };

    const theme = createTheme({
        palette: {
            primary: {
                main: '#975fff'
            },
            secondary: {
                main: '#FF7276'
            }
        }
    });

    const hasUnsignedContracts = session.unsignedContracts?.length > 0 && !localStorage.roles?.includes('Admin');

    const teacherAbsentWithoutSubstitute = () => session.teacherAbsent && !session.substituteTeacherId;
    const disableSession = () => teacherAbsentWithoutSubstitute() || session.naSession || hasUnsignedContracts;
    const disableFields = () => isPastBillingDeadline || hasUnsignedContracts;

    // A covered session (teacher absent with a substitute saved) is run by the substitute, so the header names her instead
    // of the regular teacher. The server sends her name (the substitute list may not hold her); with no name, like the
    // calendar and dashboards, it falls back to the regular teacher.
    const isCovered = session.teacherAbsent === 1 && !!session.substituteTeacherId;
    const headerTeacherName =
        isCovered && session.substituteTeacherName ? `${session.substituteTeacherName} (sub)` : session?.schoolClass?.title1TeacherName;

    return (
        <MainCard sx={{ position: 'relative' }}>
            {headerTeacherName && (
                <Typography variant="h4" align="center">
                    {`${session.name} - ${headerTeacherName}`}
                </Typography>
            )}

            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                <Tooltip title="Close">
                    <IconButton onClick={onCancel}>
                        <CancelIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <FormGroup>
                <Stack sx={{ mb: 2, mt: 2 }} spacing={1.5}>
                    <ThemeProvider theme={theme}>
                        {isPastBillingDeadline && (
                            <Alert severity="error" sx={{ fontWeight: 'bold' }}>
                                This session has passed the billing deadline.
                            </Alert>
                        )}
                        {hasUnsignedContracts && (
                            <SignContractAlert unsignedContracts={session.unsignedContracts} callbackUrl={`/session/${session.id}`} />
                        )}
                        {session !== null && !hasStudents && (
                            <Alert severity="info" color="primary">
                                No students effective on session date
                            </Alert>
                        )}

                        {session !== null && session.naSession && isFutureSession && hasStudents && (
                            <Alert severity="info" color="primary">
                                Attendance cannot be submitted for NA or future sessions
                            </Alert>
                        )}
                        {session !== null && session.naSession && !isFutureSession && hasStudents && (
                            <Alert severity="info" color="primary">
                                Attendance cannot be submitted for NA sessions
                            </Alert>
                        )}
                    </ThemeProvider>
                </Stack>
                {session !== null && substituteTeachers.length > 0 && !session.naSession && hasStudents && (
                    <TeacherAbsentSection
                        substituteTeachers={substituteTeachers}
                        initialTeacherAbsent={session.teacherAbsent === 1}
                        teacherAbsent={teacherAbsent}
                        setTeacherAbsent={setTeacherAbsent}
                        disabled={sessionConfirmed || disableFields()}
                        substituteTeacherId={session.substituteTeacherId}
                        attendanceTaken={session.attendanceTaken}
                        isSavingTeacherAttendance={isSavingTeacherAttendance}
                        setIsSavingTeacherAttendance={setIsSavingTeacherAttendance}
                        sessionId={session.id}
                        setTab={setTab}
                        setSession={setSession}
                        teacherAbsentSaveIsDirty={teacherAbsentSaveIsDirty}
                        setTeacherAbsentSaveIsDirty={setTeacherAbsentSaveIsDirty}
                    />
                )}
                {session !== null && !session.naSession && isFutureSession && hasStudents && (
                    <ThemeProvider theme={theme}>
                        <Alert severity="info" color="primary" sx={{ mb: 2, mt: 2 }}>
                            Attendance cannot be submitted for future sessions
                        </Alert>
                    </ThemeProvider>
                )}
                {sessionConfirmed && hasStudents && (
                    <ThemeProvider theme={theme}>
                        <Alert severity="info" color="secondary" sx={{ mb: 2, mt: 2 }}>
                            Cannot edit a session confirmed by supervisor
                        </Alert>
                    </ThemeProvider>
                )}

                <Dialog open={closeSessionDialogOpen}>
                    <DialogTitle>
                        Are you sure you want to close the session?
                        <Typography>You have unsaved changes.</Typography>
                    </DialogTitle>
                    <DialogActions>
                        <Button onClick={closeSession}>Yes</Button>
                        <Button onClick={() => setCloseSessionDialogOpen(false)}>No</Button>
                    </DialogActions>
                </Dialog>
            </FormGroup>

            {(loadingSession || isSavingTeacherAttendance || isSavingAttendance) && (
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open>
                    <CircularProgress color="primary" />
                </Backdrop>
            )}
            {/* Instructional session */}
            {session !== null && session.instructionalMode === 'Instructional' && hasStudents && (
                <TabContext value={tab}>
                    <Tabs
                        disabled={disableSession()}
                        value={teacherAbsent ? false || session.naSession : tab}
                        variant="fullWidth"
                        onChange={handleTabChange}
                        textColor="secondary"
                        indicatorColor="secondary"
                        aria-label="secondary tabs example"
                    >
                        <Tab
                            disabled={disableSession()}
                            value="plan"
                            label={
                                <Box display="flex" alignItems="center">
                                    PLAN
                                    {(session.lessonPlan || lessonPlanChanged) && (
                                        <CheckCircleOutline
                                            sx={{
                                                marginLeft: 1,
                                                verticalAlign: 'middle',
                                                fontSize: '1.2rem'
                                            }}
                                        />
                                    )}
                                </Box>
                            }
                        />
                        <Tab
                            disabled={disableSession()}
                            value="attendance"
                            label={
                                <Box display="flex" alignItems="center">
                                    ATTENDANCE
                                    {attendanceCompleted && (
                                        <CheckCircleOutline
                                            sx={{
                                                marginLeft: 1,
                                                verticalAlign: 'middle',
                                                fontSize: '1.2rem'
                                            }}
                                        />
                                    )}
                                </Box>
                            }
                        />
                        <Tab
                            style={{ pointerEvents: 'auto' }}
                            icon={canGrade ? null : <IconLock />}
                            iconPosition="start"
                            value="performance"
                            label={
                                <Tooltip title={performanceLockReason}>
                                    <Box display="flex" alignItems="center">
                                        <span>PERFORMANCE</span>
                                        {performanceCompleted && (
                                            <CheckCircleOutline
                                                sx={{
                                                    marginLeft: 1,
                                                    verticalAlign: 'middle',
                                                    fontSize: '1.2rem'
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Tooltip>
                            }
                            disabled={!canGrade || disableSession()}
                        />
                    </Tabs>

                    <TabPanel value="plan" index={0}>
                        <>
                            <FormControlLabel
                                disabled={teacherAbsentWithoutSubstitute() || sessionConfirmed || hasUnsignedContracts}
                                sx={{ mb: 2 }}
                                control={<Checkbox required checked={prepTimeCompleted} onChange={handlePrepSwitchChanged} />}
                                label="I did 20 minutes of prep"
                            />
                            <Autocomplete
                                disabled={disableSession() || sessionConfirmed}
                                open={open}
                                onOpen={() => {
                                    setOpen(true);
                                }}
                                onClose={() => {
                                    setOpen(false);
                                }}
                                onChange={handlePlanChange}
                                value={plan}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => option.name || ''}
                                options={options}
                                loading={loading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Lesson plan"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />
                            <Box textAlign="center" sx={{ mt: 2 }}>
                                <LoadingButton
                                    disabled={
                                        disableSession() ||
                                        (session.lessonPlan !== null && !lessonPlanChanged && !lessonPlanChangedLocally) ||
                                        plan === '' ||
                                        plan == null
                                    }
                                    variant="contained"
                                    loading={isSavingSessionPlan}
                                    loadingPosition="start"
                                    startIcon={<DoneAllIcon />}
                                    onClick={onSavePlan}
                                >
                                    Save Lesson Plan
                                </LoadingButton>
                            </Box>
                        </>
                    </TabPanel>
                    <TabPanel value="attendance" index={1}>
                        <>
                            <Collapse in={attendanceIncomplete}>
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {blockedStudents.length > 0
                                        ? 'Please enter attendance for every student who isn’t waiting on a score before submitting.'
                                        : 'Please enter attendance for all students before submitting.'}
                                </Alert>
                            </Collapse>
                            {blockedUnmarked.length > 0 && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Attendance can&apos;t be taken for {blockedNames}.{' '}
                                    {blockedUnmarked.length > 1 ? "They've" : 'This student has'} been in{' '}
                                    {session.subject === 'Math' ? 'a Math' : 'an ELA'} group for {session.assessmentScoreDays} day
                                    {session.assessmentScoreDays === 1 ? '' : 's'} this term without a Term {session.assessmentTerm}{' '}
                                    assessment score.
                                </Alert>
                            )}
                            {session === null ? (
                                <div />
                            ) : (
                                <TakeAttendanceForm
                                    event={session}
                                    onSubmit={onSubmitAttendance}
                                    isFutureSession={isFutureSession}
                                    isConfirmedSession={sessionConfirmed}
                                    disableFields={disableFields()}
                                    isSavingAttendance={isSavingAttendance}
                                    setIsSavingAttendance={setIsSavingAttendance}
                                />
                            )}
                        </>
                    </TabPanel>
                    <TabPanel value="performance" index={2}>
                        {session === null ? <div /> : <GradeStudentsForm session={session} onSubmit={onSubmitScores} />}
                    </TabPanel>
                </TabContext>
            )}

            {/* Tutoring session */}
            {session !== null && (session.instructionalMode === 'Tutoring' || session.service === 'Mentoring') && hasStudents && (
                <TutoringOrMentoringSession
                    session={session}
                    socialEmotionalSkills={socialEmotionalSkills}
                    disabled={disableSession() || isFutureSession || sessionConfirmed}
                    disableFields={disableFields()}
                    isSubmittingSession={isSavingAttendance}
                    setIsSubmittingSession={setIsSavingAttendance}
                />
            )}
        </MainCard>
    );
};

StandardSession.propTypes = {
    // The session from GET Session/{id}, with assessmentTerm and students[].assessmentBlocked
    session: PropTypes.object.isRequired,
    setSession: PropTypes.func.isRequired,
    closeSession: PropTypes.func.isRequired,
    loadingSession: PropTypes.bool,
    setLoadingSession: PropTypes.func.isRequired,
    substituteTeachers: PropTypes.array,
    socialEmotionalSkills: PropTypes.array
};

export default StandardSession;
